"use client";

/**
 * AdminSidebar — 관리자 사이드 메뉴
 *
 * 도메인에 종속되지 않도록 메뉴 구성(nav)과 현재 경로(pathname)를 props로 주입받는다.
 * 라우팅은 LinkComponent 주입으로 처리한다. (Next.js: next/link 의 Link, 기본값: <a>)
 *
 * - NavItem[] 또는 NavGroup[] 둘 다 받는다.
 * - 하위 메뉴(items)가 있으면 접고 펼 수 있는 그룹으로 렌더링한다.
 * - collapsed=true 이면 아이콘 전용 모드로 축소되고 라벨은 Tooltip 으로 보여준다.
 *
 * @example
 * <AdminSidebar nav={nav} pathname={pathname} LinkComponent={Link} logo={<Logo/>} />
 */
import { type CSSProperties, type ElementType, type ReactNode, useState } from "react";
import { ChevronRight } from "lucide-react";

import { cn } from "../../lib/utils";
import type {
  LinkComponent,
  NavGroup,
  NavItem,
  SidebarTheme,
} from "../../types";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "../ui/tooltip";

/**
 * SidebarTheme 값을 사이드바 루트에 적용할 CSS 변수 스타일로 변환한다.
 * 지정한 값만 덮어쓰고, 나머지는 전역 --sidebar* 기본값을 사용한다.
 */
export function sidebarThemeToStyle(
  theme?: SidebarTheme
): CSSProperties {
  if (!theme) return {};
  const style: Record<string, string> = {};
  if (theme.background) style["--sidebar"] = theme.background;
  if (theme.foreground) style["--sidebar-foreground"] = theme.foreground;
  if (theme.accent) style["--sidebar-accent"] = theme.accent;
  if (theme.accentForeground)
    style["--sidebar-accent-foreground"] = theme.accentForeground;
  if (theme.border) style["--sidebar-border"] = theme.border;
  if (theme.primary) style["--sidebar-primary"] = theme.primary;
  if (theme.primaryForeground)
    style["--sidebar-primary-foreground"] = theme.primaryForeground;
  if (theme.ring) style["--sidebar-ring"] = theme.ring;
  return style as CSSProperties;
}

export interface AdminSidebarProps {
  /** 메뉴 구성. 평면 항목 배열 또는 그룹 배열 */
  nav: NavItem[] | NavGroup[];
  /** 현재 활성 경로 (Next.js: usePathname()) */
  pathname?: string;
  /** 라우팅 링크 컴포넌트 (기본: <a>) */
  LinkComponent?: LinkComponent;
  /**
   * 상단 로고/브랜드 영역.
   * 함수를 넘기면 접힘 상태(collapsed)를 인자로 받아 다르게 렌더링할 수 있다.
   * (예: 펼침=풀네임, 접힘=아이콘/이니셜)
   */
  logo?: ReactNode | ((collapsed: boolean) => ReactNode);
  /** 하단 고정 영역 (예: 버전, 사용자 요약) */
  footer?: ReactNode;
  /** 아이콘 전용 축소 모드 */
  collapsed?: boolean;
  /** 메뉴 클릭(이동) 시 콜백. 모바일에서 Drawer 닫기 등에 사용 */
  onNavigate?: () => void;
  /** 사이드바 색상 테마 (프로젝트별 컬러 주입) */
  theme?: SidebarTheme;
  className?: string;
  style?: CSSProperties;
}

/**
 * nav prop을 항상 NavGroup[] 형태로 정규화한다.
 *
 * 정적 섹션 그룹(NavGroup)과 클릭해서 펼치는 대메뉴(items 를 가진 NavItem)는
 * 구조가 비슷하므로 다음 규칙으로 구분한다:
 * - 최상위 항목이 모두 "items 가 있고 / href 없고 / icon 없는" 순수 섹션이면 NavGroup[] 로 본다.
 * - 그 외(아이콘이 있거나 링크 항목이 섞여 있으면)는 평면 NavItem[] 로 보고
 *   라벨 없는 단일 그룹으로 감싼다. → 아이콘 있는 대메뉴는 접이식으로 렌더링된다.
 */
function normalizeGroups(nav: NavItem[] | NavGroup[]): NavGroup[] {
  if (nav.length === 0) return [];
  const looksLikeGroups = nav.every(
    (entry) =>
      Array.isArray((entry as NavGroup).items) &&
      (entry as NavItem).href === undefined &&
      (entry as NavItem).icon === undefined
  );
  if (looksLikeGroups) return nav as NavGroup[];
  return [{ items: nav as NavItem[] }];
}

/** 해당 항목(또는 하위 항목)이 현재 경로와 매칭되는지 판단 */
function isItemActive(item: NavItem, pathname?: string): boolean {
  if (!pathname) return false;
  if (item.href) {
    if (item.exact) return pathname === item.href;
    if (pathname === item.href) return true;
    if (item.href !== "/" && pathname.startsWith(item.href + "/")) return true;
  }
  return item.items?.some((child) => isItemActive(child, pathname)) ?? false;
}

const linkBaseClass =
  "group/menu flex items-center gap-3 rounded-md px-3 py-2 text-sm font-medium text-sidebar-foreground/80 transition-colors hover:bg-sidebar-accent hover:text-sidebar-accent-foreground outline-none focus-visible:ring-2 focus-visible:ring-sidebar-ring";
const linkActiveClass =
  "bg-sidebar-accent text-sidebar-accent-foreground font-semibold";

function Badge({ value }: { value: string | number }) {
  return (
    <span className="ml-auto inline-flex h-5 min-w-5 items-center justify-center rounded-full bg-primary px-1.5 text-xs font-medium text-primary-foreground">
      {value}
    </span>
  );
}

/** 단일 링크 항목 */
function SidebarLink({
  item,
  pathname,
  LinkComponent,
  collapsed,
  onNavigate,
  nested,
}: {
  item: NavItem;
  pathname?: string;
  LinkComponent?: LinkComponent;
  collapsed?: boolean;
  onNavigate?: () => void;
  nested?: boolean;
}) {
  const active = isItemActive(item, pathname);
  const Icon = item.icon;
  const Link = (LinkComponent ?? "a") as ElementType;

  const content = (
    <>
      {Icon ? <Icon className="size-4 shrink-0" /> : null}
      {!collapsed && <span className="truncate">{item.title}</span>}
      {!collapsed && item.badge != null && <Badge value={item.badge} />}
    </>
  );

  const className = cn(
    linkBaseClass,
    nested && !collapsed && "pl-9 text-[13px]",
    collapsed && "justify-center px-2",
    active && linkActiveClass,
    item.disabled && "pointer-events-none opacity-50"
  );

  const linkEl = item.href ? (
    <Link
      href={item.href}
      className={className}
      aria-current={active ? "page" : undefined}
      onClick={onNavigate}
    >
      {content}
    </Link>
  ) : (
    <span className={className}>{content}</span>
  );

  // 축소 모드에서는 라벨을 Tooltip 으로 표시한다.
  if (collapsed && item.icon) {
    return (
      <Tooltip>
        <TooltipTrigger asChild>{linkEl}</TooltipTrigger>
        <TooltipContent side="right">{item.title}</TooltipContent>
      </Tooltip>
    );
  }
  return linkEl;
}

/** 하위 메뉴를 가진 접이식 그룹 항목 */
function SidebarCollapsible({
  item,
  pathname,
  LinkComponent,
  onNavigate,
}: {
  item: NavItem;
  pathname?: string;
  LinkComponent?: LinkComponent;
  onNavigate?: () => void;
}) {
  const active = isItemActive(item, pathname);
  const Icon = item.icon;

  // 사용자가 직접 토글하기 전에는 활성 상태(active)에 따라 자동으로 펼친다.
  // (effect 로 동기화하지 않고 파생값으로 계산해 불필요한 재렌더를 피한다.)
  const [userOpen, setUserOpen] = useState<boolean | null>(null);
  const open = userOpen ?? active;

  return (
    <li>
      <button
        type="button"
        onClick={() => setUserOpen(!open)}
        aria-expanded={open}
        className={cn(linkBaseClass, "w-full", active && "text-sidebar-foreground")}
      >
        {Icon ? <Icon className="size-4 shrink-0" /> : null}
        <span className="truncate">{item.title}</span>
        <ChevronRight
          className={cn(
            "ml-auto size-4 shrink-0 transition-transform",
            open && "rotate-90"
          )}
        />
      </button>
      {open && (
        <ul className="mt-1 space-y-1">
          {item.items!.map((child) => (
            <li key={child.title}>
              <SidebarLink
                item={child}
                pathname={pathname}
                LinkComponent={LinkComponent}
                onNavigate={onNavigate}
                nested
              />
            </li>
          ))}
        </ul>
      )}
    </li>
  );
}

export function AdminSidebar({
  nav,
  pathname,
  LinkComponent,
  logo,
  footer,
  collapsed = false,
  onNavigate,
  theme,
  className,
  style,
}: AdminSidebarProps) {
  const groups = normalizeGroups(nav);
  const resolvedLogo = typeof logo === "function" ? logo(collapsed) : logo;

  return (
    <div
      style={{ ...sidebarThemeToStyle(theme), ...style }}
      className={cn(
        "flex h-full flex-col bg-sidebar text-sidebar-foreground",
        className
      )}
    >
      {resolvedLogo != null && (
        <div
          className={cn(
            "flex h-14 shrink-0 items-center gap-2 overflow-hidden px-4",
            collapsed && "justify-center px-3"
          )}
        >
          {resolvedLogo}
        </div>
      )}

      <nav className="flex-1 overflow-y-auto px-2 py-3">
        {groups.map((group, gi) => (
          <div key={group.title ?? gi} className={cn(gi > 0 && "mt-4")}>
            {group.title && !collapsed && (
              <p className="px-3 pb-1.5 text-xs font-semibold uppercase tracking-wider text-sidebar-foreground/50">
                {group.title}
              </p>
            )}
            <ul className="space-y-1">
              {group.items.map((item) =>
                item.items && item.items.length > 0 && !collapsed ? (
                  <SidebarCollapsible
                    key={item.title}
                    item={item}
                    pathname={pathname}
                    LinkComponent={LinkComponent}
                    onNavigate={onNavigate}
                  />
                ) : (
                  <li key={item.title}>
                    <SidebarLink
                      item={item}
                      pathname={pathname}
                      LinkComponent={LinkComponent}
                      collapsed={collapsed}
                      onNavigate={onNavigate}
                    />
                  </li>
                )
              )}
            </ul>
          </div>
        ))}
      </nav>

      {footer != null && (
        <div className="shrink-0 border-t border-sidebar-border p-3">
          {footer}
        </div>
      )}
    </div>
  );
}
